// src/pages/OrderDetails.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
    fetchOrderDetails,
    updateOrder,
    getOrderProcessLog,
    downloadOrderPdf,
    sendWhatsAppMessage,
    sendOrderEmail,
    orderIdGetInvoiceData,
    convertOrderToInvoice,
    fetchProductStatuses,
} from '../utils/api';
import { displayPrice, Modifier, OrderSummary, OrderItem, Customer } from '../utils/types';
import useCancelOrder from '../hooks/useCancelOrder';
import { hasRole } from '../utils/auth';
import { useCustomerContext } from '../context/customerContext';
import { toast } from 'react-toastify';

const API_URL = window.APP_CONFIG?.API_URL || window.APP_CONFIG?.REACT_APP_API_URL;

// =================================================================================
// 🎨 UI Component: Order Status Stepper
// =================================================================================
const OrderStatusStepper: React.FC<{ currentStatus: string }> = ({ currentStatus }) => {
    const steps = ['pending', 'processing', 'completed'];
    const currentStepIndex = steps.indexOf(currentStatus);

    const getStepClass = (stepIndex: number) => {
        if (currentStatus === 'cancel') {
            return {
                circle: 'bg-red-500 border-red-700',
                line: 'bg-red-300',
                text: 'text-red-700 font-semibold',
            };
        }
        if (stepIndex < currentStepIndex) {
            return {
                circle: 'bg-green-500 border-green-700',
                line: 'bg-green-500',
                text: 'text-gray-700',
            };
        }
        if (stepIndex === currentStepIndex) {
            return {
                circle: 'bg-blue-500 border-blue-700 animate-pulse',
                line: 'bg-gray-300',
                text: 'text-blue-700 font-semibold',
            };
        }
        return {
            circle: 'bg-gray-300 border-gray-400',
            line: 'bg-gray-300',
            text: 'text-gray-500',
        };
    };

    return (
        <div className="w-full px-4 sm:px-0 mb-8">
            <div className="flex items-center">
                {steps.map((step, index) => {
                    const { circle, line, text } = getStepClass(index);
                    return (
                        <React.Fragment key={step}>
                            <div className="flex flex-col items-center">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors duration-500 ${circle}`}>
                                    {index < currentStepIndex && currentStatus !== 'cancel' && (
                                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                                    )}
                                    {currentStatus === 'cancel' && (
                                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                    )}
                                </div>
                                <p className={`mt-2 text-xs sm:text-sm capitalize ${text}`}>{currentStatus === 'cancel' ? 'Cancelled' : step}</p>
                            </div>
                            {index < steps?.length - 1 && (
                                <div className={`flex-auto border-t-2 transition-colors duration-500 mx-2 ${line}`}></div>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
};


// =================================================================================
// 📦 UI Component: Actions Modal
// =================================================================================
const ActionsModal: React.FC<any> = ({
    isOpen, onClose, order, isActionInProgress, isCancelling, hasInvoice,
    handleDownloadPdf, handleSendWhatsAppLink, handleSendEmail,
    handleProcessOrder, handleCancelOrder, updateOrderStatus, handleConvertToInvoice,
    refreshOrderProcessLog, setOrderProcessLog, orderId, navigate,
    userId
}) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md m-4" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4 border-b pb-3">
                    <h2 className="text-xl font-bold text-gray-800">Manage Order #{order.id}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl font-bold">&times;</button>
                </div>

                <div className="space-y-4">
                    {/* --- Communication Actions --- */}
                    <div className="p-3 border rounded-md">
                        <h3 className="font-semibold text-gray-600 mb-2 text-sm">Communication</h3>
                        <div className="flex flex-col space-y-2">
                            <button onClick={handleDownloadPdf} disabled={isActionInProgress} className="btn-modal bg-purple-600 hover:bg-purple-700">Download PDF</button>
                            <button onClick={handleSendWhatsAppLink} disabled={isActionInProgress} className="btn-modal bg-teal-600 hover:bg-teal-700">Send WhatsApp to Customer</button>
                            <button onClick={handleSendEmail} disabled={isActionInProgress} className="btn-modal bg-cyan-600 hover:bg-cyan-700">Send Email to Customer</button>
                        </div>
                    </div>

                    {/* --- Status Actions --- */}
                    {hasRole(['super_admin', 'staff', 'technician']) && (
                        <div className="p-3 border rounded-md">
                            <h3 className="font-semibold text-gray-600 mb-2 text-sm">Update Status</h3>
                            <div className="grid grid-cols-2 gap-2">
                                <button onClick={async () => { await handleProcessOrder('pending'); await refreshOrderProcessLog(orderId, setOrderProcessLog); onClose(); }} disabled={isActionInProgress} className="btn-modal bg-blue-600 hover:bg-blue-700">Set Pending</button>
                                <button onClick={async () => { await handleProcessOrder('processing'); await refreshOrderProcessLog(orderId, setOrderProcessLog); onClose(); }} disabled={isActionInProgress} className="btn-modal bg-green-600 hover:bg-green-700">Set Processing</button>
                            </div>
                        </div>
                    )}

                    {/* --- Financial Actions --- */}
                    {hasRole(['super_admin', 'staff', 'technician']) && (
                        <>
                            {order.status !== 'draft' && order.status !== 'cancel' && (
                                <div className="p-3 border rounded-md">
                                    <h3 className="font-semibold text-gray-600 mb-2 text-sm">Financial</h3>
                                    {(hasRole(['super_admin', 'staff', 'technician']) || String(order.user_id) === String(userId)) && hasInvoice === null && (
                                        <button onClick={async () => { await handleConvertToInvoice(orderId, 0, 'cash'); await handleProcessOrder('completed'); await refreshOrderProcessLog(orderId, setOrderProcessLog); onClose(); }} disabled={isActionInProgress} className="btn-modal bg-indigo-600 hover:bg-indigo-700">Convert to Invoice & Mark Completed</button>
                                    )}
                                    {(hasRole(['super_admin', 'staff', 'technician']) || String(order.user_id) === String(userId)) && hasInvoice !== null && (
                                        <button onClick={() => navigate(`/invoices/${hasInvoice}`)} disabled={isActionInProgress} className="btn-modal bg-indigo-600 hover:bg-indigo-700">View Invoice #{hasInvoice}</button>
                                    )}
                                </div>
                            )}
                        </>
                    )}

                    {/* --- Cancellation --- */}
                    {order.status !== 'cancel' && (
                        <div className="p-3 border border-red-200 bg-red-50 rounded-md">
                            <button onClick={() => { handleCancelOrder(order.id, updateOrderStatus); onClose(); }} disabled={isActionInProgress || isCancelling} className="btn-modal bg-red-600 hover:bg-red-700 w-full">
                                {isCancelling ? 'Cancelling...' : 'Cancel Order'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// =================================================================================
// 🚚 UI Component: Fulfillment Modal
// =================================================================================
const FulfillmentModal: React.FC<any> = ({
    isOpen, onClose, fulfillmentType, setFulfillmentType, pickupTime, setPickupTime,
    pickerName, setPickerName, pickerPhone, setPickerPhone, pickerCarPlate, setPickerCarPlate,
    handleUpdateFulfillment, isSavingFulfillment, isActionInProgress
}) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg m-4" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4 border-b pb-3">
                    <h2 className="text-xl font-bold text-gray-800">Update Fulfillment Details</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl font-bold">&times;</button>
                </div>

                <div className="space-y-4">
                    {/* Fulfillment Method Selection */}
                    <div className="flex space-x-4 mb-4">
                        <label className="flex items-center space-x-2 cursor-pointer">
                            <input type="radio" name="fulfillment" value="delivery" checked={fulfillmentType === 'delivery'} onChange={() => setFulfillmentType('delivery')} className="form-radio h-4 w-4 text-blue-600" />
                            <span className="font-medium">Delivery</span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                            <input type="radio" name="fulfillment" value="pickup" checked={fulfillmentType === 'pickup'} onChange={() => setFulfillmentType('pickup')} className="form-radio h-4 w-4 text-blue-600" />
                            <span className="font-medium">Self Pickup</span>
                        </label>
                    </div>

                    {/* Conditional Pickup Details Form */}
                    {fulfillmentType === 'pickup' && (
                        <div className="p-4 border-t border-gray-200 space-y-3 animate-fade-in">
                            <h4 className="font-semibold text-md text-gray-700">Pickup Details</h4>
                            <div>
                                <label htmlFor="pickupDate" className="block text-sm font-medium text-gray-600">Estimated Pickup Date & Time</label>
                                <div className="flex space-x-2 mt-1">
                                    <input
                                        type="date"
                                        id="pickupDate"
                                        value={pickupTime.split('T')[0] || ''}
                                        onChange={e => {
                                            const currentTime = pickupTime.split('T')[1] || '08:00';
                                            setPickupTime(`${e.target.value}T${currentTime}`);
                                        }}
                                        className="block w-1/2 p-2 border border-gray-300 rounded-md shadow-sm text-sm"
                                    />
                                    <select
                                        id="pickupTimeSlot"
                                        value={pickupTime.split('T')[1] || '08:00'}
                                        onChange={e => {
                                            const currentDate = pickupTime.split('T')[0] || new Date().toISOString().split('T')[0];
                                            setPickupTime(`${currentDate}T${e.target.value}`);
                                        }}
                                        className="block w-1/2 p-2 border border-gray-300 rounded-md shadow-sm text-sm"
                                    >
                                        {(() => {
                                            const slots = [];
                                            // Assuming business hours are 8 AM to 6 PM (18:00)
                                            for (let hour = 8; hour <= 18; hour++) {
                                                for (let minute = 0; minute < 60; minute += 15) {
                                                    if (hour === 18 && minute > 0) continue;
                                                    const time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
                                                    slots.push(<option key={time} value={time}>{time}</option>);
                                                }
                                            }
                                            return slots;
                                        })()}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label htmlFor="pickerName" className="block text-sm font-medium text-gray-600">Picker's Name</label>
                                <input type="text" id="pickerName" placeholder="e.g., John Doe" value={pickerName} onChange={e => setPickerName(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm text-sm" />
                            </div>
                            <div>
                                <label htmlFor="pickerPhone" className="block text-sm font-medium text-gray-600">Picker's Phone</label>
                                <input type="tel" id="pickerPhone" placeholder="e.g., 0123456789" value={pickerPhone} onChange={e => setPickerPhone(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm text-sm" />
                            </div>
                            <div>
                                <label htmlFor="pickerCarPlate" className="block text-sm font-medium text-gray-600">Car Plate Number</label>
                                <input type="text" id="pickerCarPlate" placeholder="e.g., WXX 1234" value={pickerCarPlate} onChange={e => setPickerCarPlate(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm text-sm" />
                            </div>
                        </div>
                    )}

                    {fulfillmentType === 'delivery' && (
                        <div className="p-4 border-t border-gray-200">
                            <p className="text-sm text-gray-600">The order will be delivered to the customer's registered address.</p>
                        </div>
                    )}

                    {/* Save Button */}
                    <div className="mt-6 pt-4 border-t flex justify-end">
                        <button onClick={handleUpdateFulfillment} disabled={isSavingFulfillment || isActionInProgress} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed">
                            {isSavingFulfillment ? 'Saving...' : 'Update Fulfillment'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// =================================================================================
//  Main Component: Order Details Page
// =================================================================================
const OrderDetails: React.FC = () => {
    const navigate = useNavigate();

    // --- State Variables ---
    const [order, setOrder] = useState<OrderSummary | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [orderProcessLog, setOrderProcessLog] = useState<any[]>([]);
    const [isDownloadingPdf, setIsDownloadingPdf] = useState<boolean>(false);
    const [isSendingWhatsApp, setIsSendingWhatsApp] = useState<boolean>(false);
    const [isSendingEmail, setIsSendingEmail] = useState<boolean>(false);
    const [productStatuses, setProductStatuses] = useState<Map<number, boolean>>(new Map());
    const [loadingStatuses, setLoadingStatuses] = useState<boolean>(false);

    // --- Fulfillment State ---
    const [fulfillmentType, setFulfillmentType] = useState<'delivery' | 'pickup'>('delivery');
    const [pickupTime, setPickupTime] = useState('');
    const [pickerName, setPickerName] = useState('');
    const [pickerPhone, setPickerPhone] = useState('');
    const [pickerCarPlate, setPickerCarPlate] = useState('');
    const [isSavingFulfillment, setIsSavingFulfillment] = useState(false);

    // --- End Fulfillment State ---





    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isFulfillmentModalOpen, setIsFulfillmentModalOpen] = useState(false);

    // --- Hooks and Context ---
    const { orderId } = useParams<{ orderId: string }>();
    const { customers } = useCustomerContext();
    const { handleCancelOrder, isCancelling } = useCancelOrder();

    // --- User Info ---
    const userStr = localStorage.getItem('user');
    const user = userStr && userStr !== "undefined" ? JSON.parse(userStr) : {};
    const userId = user.id;

    // --- Invoice State ---
    const [hasInvoice, setHasInvoice] = useState<number | null>(null);
    const hasCheckedInvoice = useRef(false);

    // ✅ FIX: Restored original function to correctly update local order state
    const updateOrderStatus = (updatedOrder: OrderSummary) => {
        setOrder(prevOrder =>
            prevOrder && prevOrder.id === updatedOrder.id ? { ...prevOrder, status: updatedOrder.status } : prevOrder
        );
    };

    // --- API Effects for fetching data ---
    useEffect(() => {
        const checkInvoiceForOrder = async (orderId: number) => {
            if (!orderId || hasCheckedInvoice.current) return;
            hasCheckedInvoice.current = true;
            try {
                const response = await orderIdGetInvoiceData(orderId);
                if (response && response.invoice_id) {
                    setHasInvoice(response.invoice_id);
                }
            } catch (error) {
                console.error('Error checking invoice:', error);
            }
        };

        const fetchOrder = async () => {
            if (!orderId) {
                setError('Order ID is missing');
                setLoading(false);
                return;
            }
            setLoading(true);
            setError(null);
            try {
                const parsedOrderId = parseInt(orderId, 10);
                const orderData = await fetchOrderDetails(parsedOrderId);

                // console.log('Fetched Order Data:', orderData);

                // --- Populate Fulfillment State from Order Data ---
                if (orderData.fulfillment) {
                    setFulfillmentType(orderData.fulfillment.fulfillment_type || 'delivery');
                    setPickupTime(orderData.fulfillment.pickup_time_slot || '');
                    setPickerName(orderData.fulfillment.picker_name || '');
                    setPickerPhone(orderData.fulfillment.picker_phone || '');
                    setPickerCarPlate(orderData.fulfillment.picker_car_plate || '');
                } else {
                    setFulfillmentType('delivery'); // Default if fulfillment is null
                }
                // console.log('after Fetched Order Data:', orderData);

                // --- End Fulfillment State Population ---

                setOrder(orderData);
                await checkInvoiceForOrder(parsedOrderId);

                if (orderData?.items?.length > 0) {
                    // ✅ FIX: Added explicit type `number[]` to fix TypeScript error
                    const productIds: number[] = Array.from(new Set(orderData.items.map((item: OrderItem) => Number(item.product_id))));
                    if (productIds?.length > 0) {
                        setLoadingStatuses(true);
                        try {
                            const statuses = await fetchProductStatuses(productIds);
                            const statusMap = new Map<number, boolean>();
                            statuses.forEach(status => statusMap.set(status.id, status.active));
                            setProductStatuses(statusMap);
                        } catch (statusError) {
                            console.error("Failed to fetch product statuses:", statusError);
                        } finally {
                            setLoadingStatuses(false);
                        }
                    }
                }
            } catch (err: any) {
                setError(err.message || 'Failed to fetch order details');
            } finally {
                setLoading(false);
            }
        };

        fetchOrder();
    }, [orderId]);

    useEffect(() => {
        if (order && userId && !hasRole(['super_admin', 'staff', 'technician']) && String(order.user_id) !== String(userId)) {
            setError('You do not have permission to view this order.');
        }
    }, [order, userId]);

    useEffect(() => {
        const fetchOrderProcessLog = async () => {
            if (!orderId) return;
            try {
                const response = await getOrderProcessLog(parseInt(orderId));
                setOrderProcessLog(response.process_log || []);
            } catch (err: any) {
                console.error('Failed to fetch order process log:', err);
            }
        };
        fetchOrderProcessLog();
    }, [orderId]);

    // --- Handler Functions ---
    const handleProcessOrder = async (status: string) => {
        if (!order || !status || !userId) {
            toast.error("Cannot process order: Missing order details or user ID.");
            return;
        }
        // ✅ FIX: Spread the existing order to ensure all required fields are present
        const orderPayload = {
            ...order,
            status: status,
            userId: userId, // Ensure the acting user ID is present
            items: order.items, // ✅ FIX: Include existing items in the payload
            table: [],
            customer_name: customers.find(c => c.id === order.customer_id)?.name || 'Unknown Customer',
            orderType: 'order',
            tableId: '1', // Assuming default table ID
            amountToPay: order.balance,
        };

        setIsProcessing(true);

        try {
            await updateOrder(order.id, orderPayload);

            // ✅ FIX: Calling updateOrderStatus with the full order object to ensure state is correct
            updateOrderStatus({ ...order, status: status });
            toast.success(`Order status updated to ${status}!`);
        } catch (error) {
            console.error('Error updating order:', error);
            toast.error(`Failed to process order. Please try again.`);
        } finally {
            setIsProcessing(false);
        }
    };

    const refreshOrderProcessLog = async (currentOrderId: string | undefined, setLogState: React.Dispatch<React.SetStateAction<any[]>>) => {
        if (!currentOrderId) return;
        try {
            const response = await getOrderProcessLog(parseInt(currentOrderId));
            setLogState(response.process_log || []);
            toast.info('Process log refreshed!');
        } catch (err: any) {
            console.error('Failed to refresh process log:', err);
            toast.error('Failed to refresh process log.');
        }
    };

    const handleDownloadPdf = async () => {
        if (!order?.order_uuid) {
            toast.error("Order UUID is missing.");
            return;
        }
        setIsDownloadingPdf(true);
        try {
            const pdfUrl = `${API_URL}/orders/${order.order_uuid}/pdf`;
            const response = await fetch(pdfUrl, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
            if (!response.ok) throw new Error(`Failed to download PDF: ${response.statusText}`);
            const pdfBlob = await response.blob();
            const url = window.URL.createObjectURL(pdfBlob);
            window.open(url, '_blank');
            toast.success("PDF opened in new tab.");
        } catch (error: any) {
            toast.error(`Failed to download PDF: ${error.message}`);
        } finally {
            setIsDownloadingPdf(false);
        }
    };

    const handleSendWhatsAppLink = async () => {
        if (!orderId || !order || !order.order_uuid) {
            toast.error("Order details are missing for WhatsApp.");
            return;
        }
        const customer = customers?.find(c => String(c.id) === String(order.customer_id));
        if (!customer?.phone) {
            toast.warn(`Customer phone number not found.`);
            return;
        }

        let targetPhoneNumber = customer?.phone;
        if (targetPhoneNumber && targetPhoneNumber.startsWith('0')) {
            targetPhoneNumber = '6' + targetPhoneNumber;
        }

        setIsSendingWhatsApp(true);
        try {
            const settings = JSON.parse(localStorage.getItem('settings') || '{}');
            const pdfLink = `${API_URL}/orders/${order.order_uuid}/pdf`;
            const message = `*${settings.shortname || 'Our Store'}* sent you a document.\n\nOrder *${orderId}* PDF is ready.\nDownload here: ${pdfLink}\n\n*Note: This is a bot message, please do not reply.*`;
            await sendWhatsAppMessage(targetPhoneNumber, message);
            toast.info(`WhatsApp link sent to ${customer?.name}.`);
        } catch (error: any) {
            toast.error(`Failed to send WhatsApp: ${error.message}`);
        } finally {
            setIsSendingWhatsApp(false);
        }
    };

    const handleSendEmail = async () => {
        if (!orderId || !order) {
            toast.error("Order details are missing for Email.");
            return;
        }
        const customer = customers?.find(c => String(c.id) === String(order.customer_id));
        if (!customer?.email) {
            toast.warn(`Customer email not found.`);
            return;
        }
        setIsSendingEmail(true);
        try {
            const response = await sendOrderEmail(parseInt(orderId), customer?.email);
            toast.success(response.message || `Email sent to ${customer?.name}.`);
        } catch (error: any) {
            toast.error(`Failed to send email: ${error.message}`);
        } finally {
            setIsSendingEmail(false);
        }
    };

    const handleConvertToInvoice = async (orderId: string | undefined, amount: number, payMethod: string) => {
        if (!orderId) return;
        setIsProcessing(true);
        try {
            const response = await convertOrderToInvoice(parseInt(orderId), amount, payMethod);
            const newInvoiceId = response.invoice_id;
            setHasInvoice(newInvoiceId);
            toast.success(`Order successfully converted to invoice!`);
            if (newInvoiceId) {
                window.open(`/invoices/${newInvoiceId}`, '_blank');
            }
        } catch (error) {
            toast.error(`Failed to convert order to invoice.`);
        } finally {
            setIsProcessing(false);
        }
    };

    // --- Fulfillment Update Handler ---
    const handleUpdateFulfillment = async () => {
        if (!order || !userId) {
            toast.error("Cannot update fulfillment: Missing order or user details.");
            return;
        }

        setIsSavingFulfillment(true);

        // Construct the fulfillment object based on the current state
        const fulfillmentPayload = {
            fulfillment_type: fulfillmentType,
            ...(fulfillmentType === 'pickup' && {
                pickup_time_slot: pickupTime,
                picker_name: pickerName,
                picker_phone: pickerPhone,
                picker_car_plate: pickerCarPlate,
            }),
        };

        // Construct the main payload for the updateOrder API
        const orderPayload = {
            ...order, // ✅ FIX: Start with existing order data to include all required fields
            userId: userId, // Ensure the acting user ID is present
            // If fulfillment is updated, reset status to 'pending' unless it's already 'completed'
            // This ensures staff re-verifies the order.
            status: order.status !== 'completed' ? 'pending' : order.status,
            orderType: 'order',
            tableId: '1', // Assuming default table ID
            amountToPay: order.balance,
            fulfillment: fulfillmentPayload, // Add the new fulfillment data
        };

        try {
            const updatedOrderData = await updateOrder(order.id, orderPayload);
            const newOrder = updatedOrderData.data;
            setOrder(newOrder); // Update main order state with the full response from the API

            // Update local fulfillment state from the API response, not the payload
            if (newOrder.fulfillment) {
                setFulfillmentType(newOrder.fulfillment.fulfillment_type || 'delivery');
                setPickupTime(newOrder.fulfillment.pickup_time_slot || '');
                setPickerName(newOrder.fulfillment.picker_name || '');
                setPickerPhone(newOrder.fulfillment.picker_phone || '');
                setPickerCarPlate(newOrder.fulfillment.picker_car_plate || '');
            }

            toast.success("Fulfillment details updated successfully!");
            setIsFulfillmentModalOpen(false); // Close modal on success
        } catch (err: any) {
            console.error("Error updating fulfillment:", err);
            toast.error(`Failed to update fulfillment: ${err.message || 'Unknown error'}`);
        } finally {
            setIsSavingFulfillment(false);
        }
    };

    // --- Loading/Error/Not Found States ---
    if (loading) return <div className="p-4 text-center">Loading order details...</div>;
    if (error) return <div className="p-4 text-red-500 text-center">Error: {error}</div>;
    if (!order) return <div className="p-4 text-center">Order not found.</div>;

    // --- Determine if any background action is in progress ---
    const isActionInProgress = isDownloadingPdf || isSendingWhatsApp || isSendingEmail || isProcessing || isCancelling;

    const customer = customers?.find(c => String(c.id) === String(order.customer_id));

    // --- Render Component ---
    return (
        <div className="container mx-auto p-4 pb-96 md:p-6 bg-gray-50 min-h-screen">
            <style>{`.btn-modal { color: white; font-weight: 600; padding: 8px 12px; border-radius: 8px; transition: background-color 0.2s; width: 100%; text-align: center; } .btn-modal:disabled { opacity: 0.5; cursor: not-allowed; }`}</style>

            <OrderStatusStepper currentStatus={order.status} />


            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Order #{order.id}</h1>
                <div className="flex flex-col md:flex-row md:items-centerspace-x-2">
                    <button
                        onClick={() => setIsFulfillmentModalOpen(true)} className="bg-indigo-600 text-xs md:text-sm text-white font-bold py-2 px-4 rounded-lg shadow-md hover:bg-indigo-700 transition duration-150 flex items-center"
                    >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                        Fulfillment
                    </button>

                    <button
                        onClick={() => setIsModalOpen(true)}
                        className="bg-gray-800  text-xs md:text-sm text-white font-bold py-2 px-4 rounded-lg shadow-md hover:bg-gray-900 transition duration-150 flex items-center"
                    >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                        Manage Order
                    </button>
                </div>
            </div>

            <ActionsModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                order={order}
                isActionInProgress={isActionInProgress}
                isCancelling={isCancelling}
                hasInvoice={hasInvoice}
                handleDownloadPdf={handleDownloadPdf}
                handleSendWhatsAppLink={handleSendWhatsAppLink}
                handleSendEmail={handleSendEmail}
                handleProcessOrder={handleProcessOrder}
                handleCancelOrder={handleCancelOrder}
                handleConvertToInvoice={handleConvertToInvoice}
                refreshOrderProcessLog={refreshOrderProcessLog}
                setOrderProcessLog={setOrderProcessLog}
                orderId={orderId}
                navigate={navigate}
                userId={userId}
                updateOrderStatus={updateOrderStatus}
            />

            <FulfillmentModal
                isOpen={isFulfillmentModalOpen}
                onClose={() => setIsFulfillmentModalOpen(false)}
                fulfillmentType={fulfillmentType}
                setFulfillmentType={setFulfillmentType}
                pickupTime={pickupTime}
                setPickupTime={setPickupTime}
                pickerName={pickerName}
                setPickerName={setPickerName}
                pickerPhone={pickerPhone}
                setPickerPhone={setPickerPhone}
                pickerCarPlate={pickerCarPlate}
                setPickerCarPlate={setPickerCarPlate}
                handleUpdateFulfillment={handleUpdateFulfillment}
                isSavingFulfillment={isSavingFulfillment}
                isActionInProgress={isActionInProgress}
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                <div className="lg:col-span-2 p-5 border rounded-lg bg-white shadow-sm">
                    <h2 className="text-xl font-semibold mb-4 text-gray-700 border-b pb-3">Order Summary</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                        <p><span className="font-semibold text-gray-600">Customer:</span> {customer ? `${customer?.first_name || ''} ${customer?.last_name || ''}`.trim() || customer?.name || 'N/A' : 'Loading...'}</p>
                        <p><span className="font-semibold text-gray-600">Order Date:</span> {new Date(order.created_at).toLocaleString()}</p>
                        <p><span className="font-semibold text-gray-600">Status:</span>
                            <span className={`ml-2 font-bold px-2 py-1 rounded-full text-xs ${{ pending: 'bg-blue-100 text-blue-800', processing: 'bg-yellow-100 text-yellow-800', completed: 'bg-green-100 text-green-800', cancel: 'bg-red-100 text-red-800' }[order.status] || 'bg-gray-100 text-gray-800'
                                }`}>{order.status?.toUpperCase()}</span>
                        </p>

                        <p><span className="font-semibold text-gray-600">Method:</span> <span className="ml-2 font-bold text-indigo-600 bg-indigo-100 py-1 px-2 rounded-full text-xs capitalize">{fulfillmentType || 'Not Set'}
                            {fulfillmentType === 'pickup' && (
                                <span className="ml-2 text-xs font-semibold text-gray-600">Pickup Slot: {pickupTime ? new Date(pickupTime).toLocaleString() : 'N/A'}</span>)}
                        </span></p>

                        <p className="md:col-span-2 pt-2 mt-2 border-t"><span className="font-semibold text-gray-600">Total Amount:</span> <span className="font-bold text-xl text-green-700">{displayPrice(Number(order.balance))}</span></p>
                    </div>
                </div>


                {/* // abb 251012 */}
                {/* ✨ NEW: Fulfillment Details Card ✨ */}
                {/* <div className="p-5 border rounded-lg bg-white shadow-sm">
                    <h2 className="text-xl font-semibold mb-4 text-gray-700 border-b pb-3">Fulfillment Details</h2> */}

                {/* Display Current Fulfillment Info */}
                {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 text-sm mb-4">
                        <p><span className="font-semibold text-gray-600">Method:</span> <span className="ml-2 font-bold text-indigo-600 bg-indigo-100 py-1 px-2 rounded-full text-xs capitalize">{order.fulfillment?.fulfillment_type || 'Not Set'}</span></p>
                        {order.fulfillment?.fulfillment_type === 'pickup' && (
                            <>
                                <p><span className="font-semibold text-gray-600">Pickup Slot:</span> {order.fulfillment.pickup_time_slot ? new Date(order.fulfillment.pickup_time_slot).toLocaleString() : 'N/A'}</p>
                                <p><span className="font-semibold text-gray-600">Picker Name:</span> {order.fulfillment.picker_name || 'N/A'}</p>
                                <p><span className="font-semibold text-gray-600">Picker Phone:</span> {order.fulfillment.picker_phone || 'N/A'}</p>
                                <p className="md:col-span-2"><span className="font-semibold text-gray-600">Car Plate:</span> {order.fulfillment.picker_car_plate || 'N/A'}</p>
                            </>
                        )}
                    </div> */}

                {/* Button to Open Modal */}
                {/* {fulfillmentType === 'pickup' && ( */}
                {/* <button onClick={() => setIsFulfillmentModalOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded transition duration-150 w-full">Update Fulfillment</button> */}
                {/* )} */}
                {/* </div> */}

                {/* // abb 251012 */}



                {hasRole(['super_admin', 'staff', 'technician']) && (
                    <div className="lg:col-span-1 bg-white p-5 border rounded-lg shadow-sm">
                        <div className="flex justify-between items-center mb-3">
                            <h2 className="text-lg font-semibold text-gray-700">Process Log</h2>
                            <button onClick={() => refreshOrderProcessLog(orderId, setOrderProcessLog)} title="Refresh Log" className="text-blue-500 hover:text-blue-700">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h5M20 20v-5h-5M20 4h-5v5M4 20h5v-5"></path></svg>
                            </button>
                        </div>
                        {orderProcessLog?.length > 0 ? (
                            <ul className="max-h-32 overflow-y-auto scrollbar scrollbar-thumb-gray-300 text-xs space-y-2">
                                {orderProcessLog.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).map((log, index) => (
                                    <li key={index} className="flex items-start">
                                        <div className="w-2 h-2 mt-1.5 mr-2 rounded-full bg-gray-300"></div>
                                        <div>
                                            <p className={`${index === 0 ? 'font-bold text-gray-800' : 'text-gray-700'}`}>
                                                {log.action} by {log.user_name}
                                            </p>
                                            <p className="text-gray-500">{new Date(log.timestamp).toLocaleString()}</p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        ) : <p className="text-sm text-gray-500">No process log available.</p>}
                    </div>
                )}
            </div>


            <div className='pb-24'>
                <h2 className="text-xl font-bold mb-3 text-gray-800">Order Items ({order.items?.length})</h2>
                <div className="space-y-3">
                    {order.items?.map((item) => {
                        const isActive = productStatuses.get(Number(item.product_id));
                        if (typeof item.modifiers === 'string') {
                            try { item.modifiers = JSON.parse(item.modifiers); } catch { item.modifiers = []; }
                        }
                        return (
                            <div key={item.id} className="p-4 border rounded-lg bg-white shadow-sm flex flex-col sm:flex-row justify-between items-start">
                                <div className="flex-grow mb-2 sm:mb-0">
                                    <p className="font-semibold text-gray-800">
                                        {isActive ? (
                                            <Link to={`/product/${item.product_id}`} target='_blank' className="text-blue-600 hover:underline" title={`View product: ${item.payment_for}`}>
                                                {item.payment_for}
                                            </Link>
                                        ) : (
                                            <span title={`Product is inactive: ${item.payment_for}`}>{item.payment_for}</span>
                                        )}
                                        <span className="text-xs text-gray-500 ml-2 font-normal">(SKU: {item.payment_desc})</span>
                                    </p>
                                    {Array.isArray(item.modifiers) && item.modifiers?.length > 0 && (
                                        <ul className="list-disc list-inside text-xs text-gray-600 mt-1 pl-2 space-y-1">
                                            {item.modifiers.map((modifier: Modifier) => (
                                                <li key={modifier.id}>{modifier.product_name} (+{displayPrice(modifier.product_value)})</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                                <div className="text-sm text-gray-700 sm:text-right w-full sm:w-auto">
                                    <p>{displayPrice(item.unit_price)} x {item.qty}</p>
                                    <p className="font-semibold text-base text-gray-900">{displayPrice(item.unit_price * item.qty)}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default OrderDetails;